import { useLocation, useNavigate } from "react-router-dom";

import {
  useClaimVersionLock,
  useReleaseVersionLock,
  type TouchpointVersionAttributes,
} from "hooks/api/use-touchpoint-versions";
import { getTouchpointVersion } from "generated/api/touchpoint-version";
import { mapTouchpointVersionDTO } from "utilities/api-mappers";
import { TouchpointType } from "types/touchpoint";
import { BuilderContent, TouchpointStatuses } from "types";
import { BuilderTabId } from "containers/admin/clients/touchpoint/components/builder/builder-tab-nav/builder-tab-nav";
import { isLocked } from "utilities";
import { getTouchpointPaths } from "utilities/app-routes";
import { useCurrentCampaign } from "hooks/api/use-campaigns";
import { useCurrentClient } from "hooks/api/use-clients";
import { useCurrentTouchpoint } from "hooks/api/use-current-touchpoint";
import { useCurrentUser } from "hooks/api/use-current-user";
import { useTouchpointLocking } from "contexts/touchpoint-locking-context";

const touchpointVersionApi = getTouchpointVersion();

export const useBuilderAccess = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = useCurrentUser();
  const currentClient = useCurrentClient();
  const currentCampaign = useCurrentCampaign();
  const currentTouchpoint = useCurrentTouchpoint();
  const { setIsLockingModalOpen, setSelectedVersion } = useTouchpointLocking();
  const versionScope = {
    clientId: currentClient.id,
    campaignId: currentCampaign.id,
    touchpointId: currentTouchpoint.id,
  };
  const claimLockMutation = useClaimVersionLock(versionScope);
  const releaseLockMutation = useReleaseVersionLock(versionScope);

  const findVersion = async (versionId: string) => {
    const dto = await touchpointVersionApi.getOneVersion({
      ...versionScope,
      id: versionId,
    });
    return mapTouchpointVersionDTO(dto);
  };

  const accessBuilder = async (
    selectedVersion?: TouchpointVersionAttributes,
    tab?: BuilderTabId,
  ) => {
    if (!selectedVersion) return;
    const touchpointVersion = await findVersion(selectedVersion.id);

    if (isLocked(touchpointVersion as BuilderContent, currentUser)) {
      setSelectedVersion(touchpointVersion);
      setIsLockingModalOpen(true);
      return;
    }
    // Fire-and-forget; lock claim shouldn't block navigation.
    claimLockMutation.mutate(selectedVersion.id);

    const touchpointRouteType =
      selectedVersion.type === TouchpointType.EMAIL ? "email-builder" : "landing-page-builder";
    const endRoute =
      tab || (selectedVersion.status === TouchpointStatuses.PUBLISHED ? "preview" : "design");
    const relativePath = `../versions/${selectedVersion.id}`;
    const touchpointFinalRoute = `${relativePath}/${touchpointRouteType}/${endRoute}`;
    navigate(touchpointFinalRoute, { state: location.state });
  };
  const exitBuilder = async (selectedVersion: TouchpointVersionAttributes) => {
    const entryPoint = sessionStorage.getItem("builderEntryPoint");

    if (entryPoint) {
      navigate(entryPoint, { state: location.state });
      sessionStorage.removeItem("builderEntryPoint");
      return;
    }

    const { touchpointPreviewPath } = getTouchpointPaths({
      clientId: currentClient.id,
      campaignId: currentCampaign.id,
      touchpointId: currentTouchpoint.id,
    });

    try {
      const touchpoint = await findVersion(selectedVersion.id);

      if (!isLocked(touchpoint as BuilderContent, currentUser)) {
        // Fire-and-forget: don't block navigation waiting for lock release.
        // Lock release failure is non-fatal; preview will still load.
        releaseLockMutation.mutate(selectedVersion.id);
      }
    } catch {
      // If find fails, proceed to navigate anyway so the user isn't stuck
    }

    navigate(touchpointPreviewPath, { state: location.state });
  };
  return { accessBuilder, exitBuilder };
};
